import { logger } from '@/sentry.config'
import { openBlankPdfWindow, renderPdfBytesInWindow } from '@/utils/pdfBytesWindow'
import { pdfjsLib } from '@/utils/pdfjs'
import * as Sentry from '@sentry/react'
import type { Span } from '@sentry/react'
import React, { useEffect, useRef, useState } from 'react'

interface PDFViewerProps {
  pdfBytes: Uint8Array
  className?: string
  filename?: string
  /**
   * When ``false`` the control toolbar (zoom / Print / Open in New Window) is
   * hidden.
   *
   * Callers that embed the viewer inside their own chrome and provide their own
   * actions (e.g. the shipping-label modal, which has its own Print/Download)
   * pass ``false``. Defaults to ``true`` to preserve the standalone TRF
   * viewer's full toolbar.
   */
  showToolbar?: boolean
  /**
   * Called once the PDF has loaded, with the total number of pages. Callers
   * that render their own control row (e.g. the Ready-to-Send page) use this
   * to display the page count alongside their heading and action buttons.
   */
  onTotalPagesChange?: (totalPages: number) => void
}

const PDFViewer: React.FC<PDFViewerProps> = ({
  pdfBytes,
  className = '',
  filename = 'document.pdf',
  showToolbar = true,
  onTotalPagesChange,
}) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const [totalPages, setTotalPages] = useState(0)
  const [scale, setScale] = useState(1.0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [renderedPages, setRenderedPages] = useState<HTMLCanvasElement[]>([])
  // Toolbar actions report through this banner rather than the `error` state,
  // which would tear down the inline preview the user is reading.
  const [notice, setNotice] = useState<string | null>(null)
  const pdfDocRef = useRef<pdfjsLib.PDFDocumentProxy | null>(null)

  // The caller's `className` owns the viewer's height only when it passes a
  // real `height` utility (`h-*`). Emitting a hardcoded `h-screen` alongside it
  // would produce two conflicting `height` rules whose winner depends on CSS
  // source order, not intent — which made the viewer render near-viewport-tall
  // inside a bounded modal frame (e.g. h-[calc(100vh-360px)]), causing nested
  // scroll and empty gray space.
  //
  // `min-h-*` / `max-h-*` set min/max-height, NOT height, so they never
  // conflict with `h-screen` — and the viewer's `flex flex-col` + inner
  // `flex-1 overflow-auto` still needs a definite height to scroll. Callers
  // that pass only min/max height (e.g. the report viewer) must therefore KEEP
  // the `h-screen` fallback, or their page scroller breaks and the layout
  // inflates/clips. So match `h-*` while explicitly excluding `min-h-`/`max-h-`.
  const callerSetsHeight = /(^|\s)h-/.test(className)
  const rootHeightClass = callerSetsHeight ? '' : 'h-screen'

  useEffect(() => {
    const loadPDF = async () => {
      try {
        setLoading(true)
        setError(null)

        // Create a copy of the ArrayBuffer to avoid detachment issues
        const bytesCopy = pdfBytes.slice(0)

        // Load the PDF document from bytes
        const loadingTask = pdfjsLib.getDocument({ data: bytesCopy })
        const pdfDoc = await loadingTask.promise
        pdfDocRef.current = pdfDoc

        setTotalPages(pdfDoc.numPages)
        setLoading(false)
      } catch (err) {
        logger.error('PDF loading failed', {
          error: err instanceof Error ? err.message : String(err),
        })
        setError(err instanceof Error ? err.message : 'Failed to load PDF')
        setLoading(false)
      }
    }

    if (pdfBytes) {
      loadPDF()
    }
  }, [pdfBytes])

  useEffect(() => {
    if (totalPages > 0) {
      onTotalPagesChange?.(totalPages)
    }
  }, [totalPages, onTotalPagesChange])

  useEffect(() => {
    const renderAllPages = async () => {
      if (!pdfDocRef.current || totalPages === 0) {
        return
      }

      try {
        const canvases: HTMLCanvasElement[] = []

        for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
          const page = await pdfDocRef.current.getPage(pageNum)
          const canvas = document.createElement('canvas')
          const context = canvas.getContext('2d')

          const viewport = page.getViewport({ scale })
          canvas.height = viewport.height
          canvas.width = viewport.width

          const renderContext = {
            canvasContext: context!,
            viewport: viewport,
            canvas: canvas,
          }

          await page.render(renderContext).promise
          canvases.push(canvas)
        }

        setRenderedPages(canvases)
      } catch (err) {
        logger.error('PDF page rendering failed', {
          error: err instanceof Error ? err.message : String(err),
        })
        setError(err instanceof Error ? err.message : 'Failed to render pages')
      }
    }

    if (pdfDocRef.current && totalPages > 0) {
      renderAllPages()
    }
  }, [totalPages, scale])

  /**
   * Spawns a tab and paints the same bytes this viewer is showing into it as
   * canvases, with our own Download and Print controls.
   *
   * Epic's embedded browser cannot render a PDF document in a popup at all —
   * diagnostics confirmed its popups land on an opaque (``null``) origin and
   * that even a fully self-contained ``data:application/pdf`` iframe stays
   * blank, so there is no PDF renderer to hand a blob/data/served document to.
   * Painting pixels ourselves is the only thing that displays there. ``print``
   * asks the tab for the print dialog once its pages are painted.
   */
  const spawnPdfTab = (span: Span, operation: 'pdf_open' | 'pdf_print', print: boolean) => {
    span.setAttribute('pdf.filename', filename)
    span.setAttribute('pdf.size', pdfBytes.length)
    setNotice(null)

    // Opening has to happen in the click's own tick — anything deferred is
    // treated as an unsolicited popup and blocked.
    const spawned = openBlankPdfWindow()

    if (!spawned) {
      span.setAttribute('error', true)
      span.setAttribute('error.message', 'Popup blocked')
      Sentry.captureException(new Error('Popup blocked. Please allow popups for this site.'), {
        tags: { operation: `ui.action.${operation}` },
      })
      setNotice('Popups are blocked for this site. Allow them to open the requisition in a new tab.')
      return
    }

    renderPdfBytesInWindow(spawned, pdfBytes, { filename, print }).catch((err: unknown) => {
      span.setAttribute('error', true)
      if (err instanceof Error) {
        span.setAttribute('error.message', err.message)
      }
      logger.error('PDF rendering in new window failed', {
        error: err instanceof Error ? err.message : String(err),
      })
      Sentry.captureException(err, { tags: { operation: `ui.action.${operation}` } })
      setNotice('The PDF could not be rendered in the new tab.')
    })
  }

  const openPDFInNewWindow = () => {
    return Sentry.startSpan({ op: 'ui.click', name: 'Open PDF in New Window' }, (span) =>
      spawnPdfTab(span, 'pdf_open', false)
    )
  }

  /**
   * Prints from a spawned tab showing the PDF in the browser's native viewer,
   * so the output is vector, sized for paper, and free of the host page chrome.
   */
  const printPDF = () => {
    return Sentry.startSpan({ op: 'ui.click', name: 'Print PDF' }, (span) => spawnPdfTab(span, 'pdf_print', true))
  }

  const zoomIn = () => {
    setScale((prev) => Math.min(prev + 0.25, 3.0))
  }

  const zoomOut = () => {
    setScale((prev) => Math.max(prev - 0.25, 0.5))
  }

  if (loading) {
    return (
      <div className={`flex items-center justify-center p-8 ${className}`}>
        <div className="text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-indigo-600 border-r-transparent"></div>
          <p className="mt-2 text-sm text-gray-600">Loading PDF...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className={`flex items-center justify-center p-8 ${className}`}>
        <div className="text-center">
          <p className="mb-4 text-red-600">Error: {error}</p>
          <button onClick={() => setError(null)} className="rounded bg-gray-600 px-4 py-2 text-white hover:bg-gray-700">
            Try Again
          </button>
        </div>
      </div>
    )
  }

  // The inline canvas preview is the only view this component renders. Toolbar
  // actions paint their own tab from the same bytes, so the preview the user is
  // reading is never swapped out for a placeholder.
  return (
    <div className={`flex flex-col ${rootHeightClass} ${className}`.trim()}>
      {/* PDF Controls */}
      {showToolbar && (
          <div className="flex flex-shrink-0 items-center justify-between border-b bg-gray-100 p-4">
          <div className="flex items-center space-x-4">
            <span className="text-sm text-gray-600">
              {totalPages} page{totalPages !== 1 ? 's' : ''}
            </span>
          </div>

          <div className="flex items-center space-x-2">
            <button
              onClick={zoomOut}
              className="rounded border border-gray-300 bg-white px-3 py-1 hover:bg-gray-50"
              title="Zoom Out"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM13 10H7"
                />
              </svg>
            </button>
            <span className="text-sm text-gray-600">{Math.round(scale * 100)}%</span>
            <button
              onClick={zoomIn}
              className="rounded border border-gray-300 bg-white px-3 py-1 hover:bg-gray-50"
              title="Zoom In"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v6m3-3H7"
                />
              </svg>
            </button>
            <button
              onClick={printPDF}
              className="rounded border border-gray-300 bg-white px-3 py-1 hover:bg-gray-50"
              title="Print"
              aria-label="Print"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4H7v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"
                />
              </svg>
            </button>
            <button
              onClick={openPDFInNewWindow}
              className="rounded border border-indigo-600 bg-indigo-600 px-3 py-1 text-white hover:bg-indigo-700"
              title="Open in New Window"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                />
              </svg>
            </button>
          </div>
        </div>
      )}

      {notice && (
        <div
          role="alert"
          className="flex flex-shrink-0 items-center justify-between gap-4 border-b border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-800"
        >
          <span>{notice}</span>
          <button onClick={() => setNotice(null)} className="font-medium underline" title="Dismiss">
            Dismiss
          </button>
        </div>
      )}

      {/* Scrollable PDF Container */}
      <div ref={containerRef} className="min-h-0 flex-1 overflow-auto bg-gray-200 p-4">
        {/*
          `min-w-max` sizes this track to the widest page, so `items-center`
          only ever centers pages *within* it. Without it, a zoomed page
          (>~125%) is wider than the scroll container and `items-center`
          splits the overflow across both edges — and overflow past a scroll
          container's start edge is unreachable, permanently clipping the
          left of the page. Pages still center whenever they fit.
        */}
        <div className="flex min-w-max flex-col items-center space-y-4">
          {renderedPages.map((canvas, index) => (
            <div key={index} className="bg-white shadow-lg">
              <canvas
                ref={(el) => {
                  if (el) {
                    // Clone the canvas to avoid issues with multiple renders
                    const context = el.getContext('2d')
                    if (context) {
                      el.width = canvas.width
                      el.height = canvas.height
                      context.drawImage(canvas, 0, 0)
                    }
                  }
                }}
                className="block"
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

export default PDFViewer
